/**
 * Catalog label cache — localStorage persistence layer.
 *
 * When the masters API returns items with labels (populated from the catalog_translations
 * DB table), we persist them here. On subsequent page loads where the API fails or the
 * DB translations are temporarily unavailable, the slice's rejected handler restores the
 * cached data so the user sees human-readable labels instead of raw codes.
 *
 * Cache is per catalog name. The stored value is always a plain array of the same shape
 * that the slice stores in Redux, so it can be dropped in directly.
 */

const PREFIX = 'gts_cat_v1_';

const cacheKey = (name: string) => `${PREFIX}${name}`;

/**
 * Persist catalog items to localStorage. Silently no-ops if the array is empty
 * (we never want to overwrite good data with an empty array).
 */
export function setCatalogCache(name: string, data: any[]): void {
  if (!Array.isArray(data) || data.length === 0) return;
  try {
    localStorage.setItem(cacheKey(name), JSON.stringify(data));
  } catch {
    // Quota exceeded or private-browsing restriction — ignore.
  }
}

/**
 * Retrieve cached catalog items from localStorage. Returns null when the cache
 * is missing, malformed, or empty so callers can fall back gracefully.
 */
export function getCatalogCache(name: string): any[] | null {
  try {
    const raw = localStorage.getItem(cacheKey(name));
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) && parsed.length > 0 ? (parsed as any[]) : null;
  } catch {
    return null;
  }
}
